'use client';

import { ForwardRefRenderFunction, ReactNode, useImperativeHandle, useRef } from 'react';
import { forwardRef } from 'react';
import { Select, SelectProps } from '../Select';
import { twMerge } from '@tui-cruises/mein-schiff-web-tailwind-config';
import { InputFieldLabel } from '../InputField/InputFieldLabel';
import { InputFieldNote } from '../InputField/InputFieldNote';
import { InputFieldError } from '../InputField/InputFieldError';
export type SelectFieldProps = Omit<SelectProps, 'required'> & {
  label?: ReactNode;
  note?: ReactNode;
  error?: string;

  /**
   * Use `label` if you don't want to add the `required` attribute to the select element itself.
   * This will avoid highlighting the select as invalid, when there is no value.
   * This is usually a user friendlier way than highlighting the field invalid on initial page load.
   */
  required?: boolean | 'label';
  normalizeHeight?: boolean;
};
const SelectFieldRenderFunction: ForwardRefRenderFunction<HTMLSelectElement | null, SelectFieldProps> = ({
  label,
  note,
  error,
  required,
  className,
  normalizeHeight,
  ...attrs
}, forwardedRef) => {
  const selectRef = useRef<HTMLSelectElement>(null);
  useImperativeHandle<HTMLSelectElement | null, HTMLSelectElement | null>(forwardedRef, () => selectRef.current);
  return <div className={twMerge('block space-y-1', className)} data-sentry-component="SelectFieldRenderFunction" data-sentry-source-file="SelectField.tsx">
      <label className="block space-y-1">
        {label && <InputFieldLabel asChild>
            <div>
              {label}
              {required ? '*' : ''}
            </div>
          </InputFieldLabel>}
        <Select {...attrs} normalizeHeight={normalizeHeight} required={typeof required === 'boolean' ? required : undefined} invalid={error} ref={selectRef} data-sentry-element="Select" data-sentry-source-file="SelectField.tsx" />
        {!error && note && <InputFieldNote>{note}</InputFieldNote>}
      </label>

      {error && <InputFieldError>{error}</InputFieldError>}
    </div>;
};
export const SelectField = forwardRef<HTMLSelectElement | null, SelectFieldProps>(SelectFieldRenderFunction);